// app/features/industry-workflows/components/IndustryWorkflowCardComponent.tsx
'use client';

import Link from 'next/link';

import type { IndustryWorkflowListItem } from '../industry-workflow.interface';

interface Props {
  workflow: IndustryWorkflowListItem;
}

export function IndustryWorkflowCardComponent({ workflow }: Props) {
  const statusColors = {
    completed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    pending: 'bg-yellow-100 text-yellow-800',
    context_loading: 'bg-blue-100 text-blue-800',
    generating_problem_definition: 'bg-blue-100 text-blue-800',
    generating_workflows: 'bg-blue-100 text-blue-800',
    generating_content: 'bg-blue-100 text-blue-800',
    assembling: 'bg-blue-100 text-blue-800',
    storing: 'bg-blue-100 text-blue-800',
  };

  const statusLabels = {
    completed: 'Ready',
    failed: 'Failed',
    pending: 'Pending',
    context_loading: 'Loading...',
    generating_problem_definition: 'Analyzing...',
    generating_workflows: 'Creating...',
    generating_content: 'Writing...',
    assembling: 'Finalizing...',
    storing: 'Saving...',
  };

  return (
    <Link href={`/industries/workflows/${workflow.id}`}>
      <div className="group relative overflow-hidden rounded-lg border border-gray-200 bg-white p-6 transition-all hover:border-blue-500 hover:shadow-lg">
        {/* Status Badge */}
        <div className="absolute top-4 right-4">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[workflow.generation_status]}`}>
            {statusLabels[workflow.generation_status]}
          </span>
        </div>

        {/* Industry Badge */}
        {workflow.input_industry_name && (
          <div className="mb-3">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
              {workflow.input_industry_name}
            </span>
          </div>
        )}

        {/* Title */}
        <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
          {workflow.title || 'Untitled Workflow'}
        </h3>

        {/* View Count */}
        <div className="mt-4 flex items-center text-sm text-gray-500">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          {workflow.view_count} views
        </div>

        {/* Arrow Icon */}
        <div className="absolute bottom-4 right-4 text-gray-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </Link>
  );
}

